import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setLanguage, type Lang } from '@/i18n';
import { AuthContext, type AuthContextValue, type AuthState } from './authContext';
import { clearPersistedLive } from '@/features/live/livePersist';
import {
  restampPushSubscription,
  removePushSubscriptionOnSignOut,
} from '@/features/push/pushClient';
import { onForcedLogout } from './tokenStore';
import { adoptImpersonationFromFragment } from './impersonation';
import { getSigninLangChoice, clearSigninLangChoice } from './signinLangChoice';
import {
  bootstrap,
  getMe,
  login as loginRequest,
  logout as logoutRequest,
  updateProfile as updateProfileRequest,
  type User,
} from './authClient';

/** Adopt the signed-in user's server language and persist it locally (C, layer 2). */
function adoptUserLanguage(user: User): void {
  const lang: Lang = user.language === 'so' ? 'so' : 'en';
  setLanguage(lang, { persist: true });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const queryClient = useQueryClient();
  const bootstrapped = useRef(false);

  // Silent session restore, exactly once (StrictMode double-invokes effects).
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    let active = true;

    // 🔴 AN IMPERSONATION TOKEN MUST WIN OVER A REFRESH COOKIE, AND THE ORDER IS
    // THE WHOLE GUARANTEE.
    //
    // A staff member opening a customer's portal in a browser where they happen
    // to hold their own session would otherwise silently see THEIR fleet and
    // believe it was the customer's — a screen confidently showing the wrong
    // tenant's vehicles, which is close to the worst failure this product can
    // produce. Adopting the fragment FIRST makes that unrepresentable rather
    // than unlikely.
    //
    // `getMe` rather than `bootstrap`: bootstrap begins by exchanging the
    // refresh cookie, which would overwrite the impersonation token we just
    // installed with the staff member's own session.
    const impersonating = adoptImpersonationFromFragment();

    void (impersonating ? getMe().catch(() => null) : bootstrap()).then((result) => {
      if (!active) return;
      if (result && result.kind === 'tenant') {
        adoptUserLanguage(result.user);
        setState({ status: 'authenticated', user: result.user });
        // A restored session may belong to a different user than the one who
        // enabled push in this browser. Re-stamp the subscription's ownership to
        // this principal (66j). Fire-and-forget — never blocks the restore. Not
        // while impersonating: a staff device must never be re-stamped to the
        // viewed tenant (the server also refuses writes on a read-only session).
        if (!impersonating) void restampPushSubscription();
      } else if (result && result.kind === 'staff') {
        // A staff account holds a session on the customer origin. No fleet to
        // show — drop to the staff branch (RequireAuth renders the panel). Clear
        // any on-device last-known fleet a prior tenant left on this device.
        clearPersistedLive();
        setState({ status: 'staff' });
      } else {
        // Boot landed on the login screen (no/expired refresh cookie). This is
        // the silent-expiry path that fires NO forced-logout event, so clear the
        // on-device last-known fleet here too — defence in depth beneath the
        // per-principal key (livePersist).
        clearPersistedLive();
        setState({ status: 'unauthenticated' });
      }
    });
    return () => {
      active = false;
    };
  }, []);

  // A refresh failure anywhere (expiry / revocation / reuse-burn) forces logout:
  // drop to the login screen and clear any cached tenant data.
  useEffect(
    () =>
      onForcedLogout(() => {
        queryClient.clear();
        // Drop the on-device last-known fleet too, so a shared device never
        // flashes a previous session's vehicles on the next login.
        clearPersistedLive();
        // Any pending sign-in language tap dies with the session (66q).
        clearSigninLangChoice();
        setState({ status: 'unauthenticated' });
      }),
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      async login(phone, password) {
        // Before minting the next session, drop any on-device last-known fleet a
        // previous principal left on this device — a shared phone must never seed
        // the incoming user's map with the outgoing user's vehicles. The
        // per-principal key already makes a foreign blob unreadable; this ensures
        // it is not even present. (An offline different-user login can't occur —
        // login() requires the network.)
        clearPersistedLive();
        let result: Awaited<ReturnType<typeof loginRequest>>;
        try {
          result = await loginRequest(phone, password);
        } catch (err) {
          // 66q (verifier) — a FAILED sign-in drops the pending tap, like the app,
          // so person A's tap cannot ride into person B's later sign-in.
          clearSigninLangChoice();
          throw err;
        }
        if (result.kind === 'staff') {
          // Valid staff credentials at the customer door. No fleet — the panel
          // takes over via RequireAuth once the Login screen navigates away. A
          // staff session has no `users.language` to write, so a pending sign-in
          // choice is simply dropped (66q), never PATCHed.
          clearSigninLangChoice();
          setState({ status: 'staff' });
          return;
        }
        // 66q — an EXPLICIT EN/SO tap on the sign-in screen wins over the account
        // language AND is written to the account (Mahad, 27 Sep). The marker is a
        // one-shot: read and cleared here, so a stale tap can never rewrite the
        // next signer-in's account on a shared device. Consumed ONLY here — never
        // on bootstrap/restore, where a leftover marker must not touch the account.
        const choice = getSigninLangChoice();
        clearSigninLangChoice();
        const writeChoice = choice != null && choice !== result.user.language;
        if (writeChoice) {
          // Keep the chosen language on screen now; adopt the server's own answer
          // once the write lands.
          setLanguage(choice, { persist: true });
        } else {
          adoptUserLanguage(result.user);
        }
        setState({ status: 'authenticated', user: result.user });
        // Re-stamp any push subscription this browser already holds to the user who
        // just signed in — if user B logs in where user A enabled push, the row
        // must become B's before the next event fires (66j). Silent, best-effort,
        // never awaited so it cannot delay sign-in.
        void restampPushSubscription();
        if (writeChoice) {
          try {
            const updated = await updateProfileRequest({ language: choice });
            // Adopt the SERVER's answer — the row the next boot will read.
            adoptUserLanguage(updated);
            setState({ status: 'authenticated', user: updated });
          } catch {
            // The write failed: keep the chosen language for THIS session (already
            // applied above); the next launch adopts the server value. No silent
            // retry loop — honest over eventually-right.
          }
        }
      },
      async updateProfile(patch) {
        const user = await updateProfileRequest(patch);
        // Adopt from the SERVER's answer, never from the value we sent. The
        // server row is the source of truth `adoptUserLanguage` reads on every
        // subsequent boot; applying our own optimistic value here would make the
        // UI agree with itself and disagree with the next reload — which is
        // precisely the defect being fixed.
        adoptUserLanguage(user);
        setState({ status: 'authenticated', user });
      },
      async refreshUser() {
        const result = await getMe();
        // Adopt only a tenant result. A staff/impersonation body has no tenant
        // `user` to place in `authenticated`, and this method is only ever called
        // from a tenant-only surface (the command-PIN panel), so anything else is
        // left untouched rather than forced into the wrong branch.
        if (result.kind === 'tenant') {
          setState({ status: 'authenticated', user: result.user });
        }
      },
      async signOut() {
        // Remove this browser's push subscription server-side BEFORE the session is
        // cleared — the DELETE needs the current bearer/cookie, which `logoutRequest`
        // revokes (66j). Best-effort and time-bounded: sign-out must never hang or
        // fail on it. The browser-side PushSubscription is kept so the next sign-in
        // can re-stamp without a fresh permission prompt.
        await removePushSubscriptionOnSignOut();
        await logoutRequest();
        queryClient.clear();
        clearPersistedLive();
        // A pending sign-in tap must not survive into the next user's login (66q).
        clearSigninLangChoice();
        setState({ status: 'unauthenticated' });
      },
    }),
    [state, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
